import { createSelector } from 'reselect';
import {
  isVoiceRecordingListPageEnabled,
  isTtsDictionaryEnabled,
} from 'selectors/featureToggles';
import { hasAnyPermission } from 'utils/permissions';
import permissionConstants from 'xm-constants/permissions';

import {
  pruneItemsAndGroups,
  selectFeatureToggleMap,
  selectLoggedInUser,
  selectSubMenuId,
} from './util';

export const getSupportDeveloperMenu = createSelector(
  [selectLoggedInUser],
  loggedInUser => !loggedInUser.isSuperAdmin,
);

export const getWorkflowsNavItem = createSelector(
  [selectSubMenuId],
  subMenuId => ({
    id: 'NAV_WORKFLOWS',
    to: 'WorkflowListPlace:/',
    permission: permissionConstants.VIEW_MENUITEM_APPLICATION_MANAGER,
    isActive: () =>
      subMenuId === 'WorkflowListPlace' ||
      subMenuId === 'manage-relevance-engines',
    selectorId: 'WorkflowListPlace',
  }),
);

export const getFloodControlNavItem = createSelector(
  [getSupportDeveloperMenu],
  supportDeveloperMenu =>
    supportDeveloperMenu && {
      id: 'FLOOD_CONTROL',
      to: 'FloodControlPlace:/',
      permission: permissionConstants.VIEW_MENU_DEVELOPER,
      selectorId: 'FloodControlPlace',
    },
);

export const getXAgentNavItem = createSelector(
  [getSupportDeveloperMenu],
  supportDeveloperMenu =>
    supportDeveloperMenu && {
      id: 'NAV_AGENTS',
      to: 'XAgentManagerPlace:null',
      permission: permissionConstants.VIEW_MENU_DEVELOPER,
      selectorId: 'XAgentManagerPlace',
    },
);

function getDeveloperNav(
  loggedInUser,
  subMenuId,
  featureToggleMap,
  ttsDictionaryEnabled,
  supportDeveloperMenu,
  workflowsNavItem,
  floodControlNavItem,
  xAgentNavItem,
  voiceRecordingListPageEnabled,
) {
  const { permissions } = loggedInUser.loggedInUserProfile;
  const isNewOAuthTokensEnabled =
    featureToggleMap.REACT_OAUTH_TOKENS_PAGE === 'true';

  const navItemGroups = [
    {
      id: 'NAV_WORKFLOWS',
      items: [
        workflowsNavItem,
        {
          id: 'NAV_WORKFLOW_LIBRARY',
          to: 'IntegrationManagerPlace:/',
          isActive: () => subMenuId === 'IntegrationManagerPlace', // Match all subroutes
          permission: permissionConstants.VIEW_MENUITEM_APPLICATION_MANAGER,
          selectorId: 'IntegrationManagerPlace',
        },
      ],
    },
    {
      id: 'WORKFLOW_TOOLS',
      items: [
        floodControlNavItem,
        {
          id: 'NAV_CONFERENCE_BRIDGES_PAGE',
          to: 'ExternalConferenceBridgeListPage',
          permission: permissionConstants.ABILITY_ACT_VIEW_CONFERENCE_BRIDGES,
          isActive: () => subMenuId === 'conference-bridges',
          selectorId: 'conference-bridges',
        },
        xAgentNavItem,
      ],
    },
    {
      id: 'NAV_CUSTOMIZATION',
      items: [
        !ttsDictionaryEnabled && {
          id: 'NAV_TTS_DICTIONARY',
          to: 'TTSDictionaryPlace:null',
          permission: permissionConstants.ABILITY_ACT_VIEW_TTSDICTIONARY,
          selectorId: 'TTSDictionaryPlace',
        },
        ttsDictionaryEnabled && {
          id: 'NAV_TTS_DICTIONARY',
          to: 'TtsDictionaryPage',
          permission: permissionConstants.ABILITY_ACT_MANAGE_TTS_DICTIONARY,
          isActive: () => subMenuId === 'tts-dictionary-page',
          selectorId: 'tts-dictionary-page',
        },
      ],
    },
    {
      id: 'NAV_PHONE_RECORDINGS',
      items: [
        voiceRecordingListPageEnabled && {
          id: 'NAV_VOICE_RECORDINGS',
          to: 'VoiceRecordingListPage',
          permission: perms =>
            hasAnyPermission(
              perms,
              permissionConstants.VIEW_MENUITEM_PHONE_RECORDINGS,
              permissionConstants.ABILITY_ACT_MANAGE_VOICE_RECORDINGS,
            ),
          isActive: () => subMenuId === 'voice-recordings', // Match all subroutes
          selectorId: 'voice-recordings',
        },
        !voiceRecordingListPageEnabled && {
          id: 'NAV_MANAGE_RECORDINGS',
          to: 'CocoonPlace:/xmatters/Subscriptions/PhoneRecordings/PhoneRecordings.do',
          permission: permissionConstants.VIEW_MENUITEM_PHONE_RECORDINGS,
          isActive: () => subMenuId === 'PhoneRecordings', // Match all subroutes
          selectorId: 'PhoneRecordings',
        },
        !voiceRecordingListPageEnabled && {
          id: 'NAV_ADD_PHONE_RECORDINGS',
          to: 'CocoonPlace:/xmatters/Subscriptions/AddPhoneRecording/AddPhoneRecording.do',
          permission: permissionConstants.VIEW_MENUITEM_ADD_PHONE_RECORDINGS,
          isActive: () => subMenuId === 'AddPhoneRecording',
          selectorId: 'AddPhoneRecording',
        },
      ],
    },
    supportDeveloperMenu && {
      id: 'NAV_AUTHENTICATION',
      items: [
        !isNewOAuthTokensEnabled && {
          id: 'NAV_OAUTH',
          to: 'OauthCredentialsPlace:null',
          permission: permissionConstants.VIEW_MENU_DEVELOPER,
          isActive: () => subMenuId === 'oauth-credentials',
          selectorId: 'oauth-credentials',
        },
        isNewOAuthTokensEnabled && {
          id: 'NAV_OAUTH',
          to: 'OauthCredentialsPage',
          permission: permissionConstants.VIEW_MENU_DEVELOPER,
          isActive: () => subMenuId === 'oauth-credentials-page',
          selectorId: 'oauth-credentials-page',
        },
      ],
    },
  ];

  return pruneItemsAndGroups(navItemGroups, permissions);
}

export default createSelector(
  [
    selectLoggedInUser,
    selectSubMenuId,
    selectFeatureToggleMap,
    isTtsDictionaryEnabled,
    getSupportDeveloperMenu,
    getWorkflowsNavItem,
    getFloodControlNavItem,
    getXAgentNavItem,
    isVoiceRecordingListPageEnabled,
  ],
  getDeveloperNav,
);
